/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import moment from 'moment';
import { currencies, exchange } from '../../../../utilities/constants';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import { formatNumber, formatNumberWithComma } from '../../../../utilities/formatHelpers';
import { YIELD_DEPOSIT_DETAILS } from '../../queries';

const DepositDetails = ({ details, rate }) => {
  const { t: translate } = useTranslation();

  const [duration, setDuration] = useState(0);
  const [passedDuration, setPassedDuration] = useState(0);

  const { data: depositDetails, refetch: refetchDepositDetails } = useQuery(
    [YIELD_DEPOSIT_DETAILS, { contractId: details?.id }],
    {
      onSettled: (data, error) => {
        if (!error) {
          const activationDate = moment(data?.contractAttributes?.activatedAt);
          const expiryDate = moment(data?.product?.expirationDate);
          const days = expiryDate.diff(activationDate, 'days');

          const currentDate = moment();
          const passedDays = expiryDate.diff(currentDate, 'days');

          setDuration(days);
          setPassedDuration(passedDays);
        }
      },
      enabled: false,
    }
  );

  const shouldReceiveUSDC = rate?.price > depositDetails?.contractAttributes?.btcBuyAt;

  useEffect(() => {
    refetchDepositDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  const renderTitle = () => (
    <div className="w-100 d-flex flex-row justify-content-between align-items-center">
      <p className="text-dark text-f5">{translate('yield_offer_details_modal_title')}</p>
      <div className="d-flex flex-row">
        <p className="text-f6 text-shadow me-2">{translate('oxygen_yield_current_BTC_label')}</p>
        <p className="text-f6 text-dark">{`${formatNumberWithComma(
          formatNumber(rate?.price, exchange.FIAT_VALUE)
        )} ${currencies.USDC.fullName}`}</p>
      </div>
    </div>
  );

  return (
    <ModalBodyLayout withBack={false} renderTitle={renderTitle} isScrollable>
      <div className="d-flex flex-row justify-content-between mt-4">
        <div className="d-flex flex-column">
          <p className="text-primary text-f4 text-bold">
            {`${formatNumber(
              depositDetails?.contractAttributes?.annualizedYieldPct,
              exchange.FIAT_VALUE
            )}%`}
          </p>
          <p className="text-shadow text-f6">
            {translate('yield_offer_details_modal_annualized_label')}
          </p>
        </div>
        <div className="d-flex flex-column align-items-end">
          <p className="text-dark text-f4 text-bold">
            {translate('yield_offer_details_modal_days_label').replace('__DAYS_NUMBER__', duration)}
          </p>
          <p className="text-shadow text-f6">
            {translate('yield_offer_details_modal_duration_label')}
          </p>
        </div>
      </div>
      <div className="d-flex flex-row justify-content-between mt-4">
        <div className="d-flex flex-column">
          <p className="text-dark text-f5 text-bold">
            {depositDetails && depositDetails?.product?.linkedPrice
              ? `${formatNumberWithComma(
                  formatNumber(depositDetails?.product?.linkedPrice, exchange.FIAT_VALUE)
                )} ${currencies.USDC.fullName}`
              : `${formatNumberWithComma(formatNumber(0, exchange.FIAT_VALUE))} ${
                  currencies.USDC.fullName
                }`}
          </p>
          <p className="text-shadow text-f6">
            {translate('yield_offer_details_modal_linked_price_label')}
          </p>
        </div>
        <div className="d-flex flex-column align-items-end">
          <p className="text-dark text-f5 text-bold">
            {details &&
              depositDetails?.product?.expirationDate &&
              moment(depositDetails?.product?.expirationDate, 'YYYYMMDDh:mm:ss').format(
                'DD/MM/YYYY'
              )}
          </p>
          <p className="text-shadow text-f6">
            {translate('yield_offer_details_modal_expiry_date_label')}
          </p>
        </div>
      </div>
      <div className="mt-5">
        <p className="text-shadow text-f6">
          {translate('yield_deposit_details_modal_amount_label')}
        </p>
        <p className="text-dark text-f3 text-bold mt-2">
          {depositDetails && depositDetails?.contractAttributes?.amount
            ? `${formatNumberWithComma(
                formatNumber(depositDetails?.contractAttributes?.amount, exchange.FIAT_VALUE)
              )} ${currencies.USDC.fullName}`
            : `${formatNumberWithComma(formatNumber(0, exchange.FIAT_VALUE))} ${
                currencies.USDC.fullName
              }`}
        </p>
      </div>
      <div className="mt-5">
        <div className="d-flex flex-row justify-content-between align-items-center">
          <p className="text-dark text-f5">
            <span className="text-primary">{passedDuration}</span>
            {translate('yield_deposit_details_modal_passed_duration_label').replace(
              '__DURATION__',
              duration
            )}
          </p>
          <p className="text-shadow text-f6">
            {translate('yield_offer_details_modal_expiry_date_label')}
          </p>
        </div>
        <div className="progress mt-3 bg-disabled">
          <div
            className="progress-bar"
            style={{
              width: `${((duration - passedDuration) * 100) / duration}%`,
            }}
          />
        </div>
      </div>
      <p className="text-shadow text-f6 text-bold mt-5">
        {translate('yield_contirm_invest_received_amount_note')}
      </p>
      <div className="d-flex flex-column bg-light rounded-extra p-3 mt-2">
        <div className="d-flex flex-row justify-content-between">
          <div className="d-flex flex-row justify-content-start align-items-center">
            <div className="w-25 me-2">
              <img
                src={`${
                  shouldReceiveUSDC
                    ? `./assets/icons/${currencies.USDC.fullName}.svg`
                    : `./assets/icons/${currencies.USDC.fullName}-disabled.svg`
                }`}
                className="w-100"
                alt={currencies.USDC.fullName}
              />
            </div>
            <p
              className={`text-nowrap ${
                shouldReceiveUSDC ? 'text-dark' : 'text-shadow'
              }`}>{`${formatNumberWithComma(
              formatNumber(depositDetails?.contractAttributes?.usdcPaidAmount, exchange.FIAT_VALUE)
            )} ${currencies.USDC.fullName}`}</p>
          </div>
          <p
            className={`text-nowrap ${
              shouldReceiveUSDC ? 'text-dark' : 'text-shadow'
            }`}>{`${formatNumberWithComma(
            formatNumber(depositDetails?.contractAttributes?.usdcProfit, exchange.FIAT_VALUE)
          )} ${currencies.USDC.fullName}`}</p>
        </div>
        <div className="d-flex flex-row justify-content-between mt-1">
          <p className="text-shadow text-f6">
            {translate('yield_offer_details_modal_get_token_more_note').replace(
              '__PRICE__',
              formatNumberWithComma(
                formatNumber(depositDetails?.product?.linkedPrice, exchange.FIAT_VALUE)
              )
            )}
          </p>
          <p className="text-shadow text-f6">{translate('yield_offer_details_modal_profit')}</p>
        </div>

        <div className="d-flex flex-row justify-content-center align-items-center my-2">
          <hr className="m-0 text-shadow w-100" />
          <p className="text-shadow mx-2">{translate('yield_offer_details_modal_or')}</p>
          <hr className="m-0 text-shadow w-100" />
        </div>

        <div className="d-flex flex-row justify-content-between">
          <div className="d-flex flex-row justify-content-start align-items-center">
            <div className="w-25 me-2">
              <img
                src={`${
                  !shouldReceiveUSDC
                    ? `./assets/icons/${currencies.BTC.icon}.svg`
                    : `./assets/icons/${currencies.BTC.icon}-disabled.svg`
                } `}
                className="w-100"
                alt={currencies.USDC.fullName}
              />
            </div>
            <p
              className={`text-nowrap ${
                !shouldReceiveUSDC ? 'text-dark' : 'text-shadow'
              }`}>{`${formatNumberWithComma(
              formatNumber(
                depositDetails?.contractAttributes?.btcPaidAmount,
                currencies.BTC.decimal
              )
            )} BTC`}</p>
          </div>
          <p
            className={`text-nowrap ${
              !shouldReceiveUSDC ? 'text-dark' : 'text-shadow'
            }`}>{`${formatNumberWithComma(
            formatNumber(depositDetails?.contractAttributes?.btcBuyAt, exchange.FIAT_VALUE)
          )} ${currencies.USDC.fullName}`}</p>
        </div>
        <div className="d-flex flex-row justify-content-between mt-1">
          <p className="text-shadow text-f6">
            {translate('yield_offer_details_modal_get_token_less_note').replace(
              '__PRICE__',
              formatNumberWithComma(
                formatNumber(depositDetails?.product?.linkedPrice, exchange.FIAT_VALUE)
              )
            )}
          </p>
          <p className="text-shadow text-f6">
            {translate('yield_offer_details_modal_buy_at_label')}
          </p>
        </div>
      </div>
    </ModalBodyLayout>
  );
};

export default DepositDetails;

DepositDetails.propTypes = {
  details: PropTypes.object.isRequired,
  rate: PropTypes.object,
};

DepositDetails.defaultProps = {
  rate: {},
};
